import {
  Button,
  Checkbox,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Typography,
} from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { ReferralData } from "../../types/admin/adminTypes";
import { IncentiveType, ReferralAction } from "../../types/admin/adminEnums";

type AcceptReferralModalProps = {
  openModal: boolean;
  referralData: ReferralData | null;
  onModalUpdate: (values: any) => void;
  onCloseModal: () => void;
};

const AcceptReferralModal = ({
  openModal,
  referralData,
  onModalUpdate,
  onCloseModal,
}: AcceptReferralModalProps) => {
  const [form] = Form.useForm();
  const [incentiveType, setIncentiveType] = useState<IncentiveType | null>();

  useEffect(() => {
    form.setFieldsValue(referralData);
    setIncentiveType(referralData?.incentiveType);
  }, [referralData]);

  const onFormValuesChange = (values: any) => {
    if (values?.hasOwnProperty("incentiveType")) {
      setIncentiveType(values["incentiveType"]);
    }
  };

  const handleFormSubmit = (values: any) => {
    onModalUpdate({
      ...values,
      Id: referralData?.Id,
      ...(referralData?.referralAction === ReferralAction.PENDING
        ? { referralAction: ReferralAction.ACCEPTED }
        : {}),
    });

    form.resetFields();
    setIncentiveType(undefined);
    onCloseModal();
  };

  const handleCloseModal = () => {
    form.resetFields();
    setIncentiveType(undefined);
    onCloseModal();
  };

  return (
    <StyledModal
      open={openModal}
      centered
      width={400}
      style={{ paddingTop: 5 }}
      onOk={form.submit}
      onCancel={handleCloseModal}
      okText={
        referralData?.referralAction !== ReferralAction.PENDING
          ? "Save"
          : "Save & Accept"
      }
    >
      <Flex align="center" justify="center">
        <Typography.Text
          style={{ margin: "1rem 0", fontWeight: "bold", fontSize: "1.4rem" }}
        >
          Accept Referral
        </Typography.Text>
      </Flex>
      <Form
        layout={"vertical"}
        form={form}
        name="acceptReferralForm"
        initialValues={referralData ?? {}}
        onValuesChange={onFormValuesChange}
        onFinish={handleFormSubmit}
        style={{ maxWidth: 350 }}
      >
        <Form.Item
          name="referralOnboardedDate"
          label="Referral Onboarded Date"
          rules={[{ required: true, message: "" }]}
        >
          <DatePicker
            placeholder="Choose Referral Onboarded Date"
            style={{ width: 350 }}
          />
        </Form.Item>
        <Form.Item
          name="incentiveEndDate"
          label="Incentive End Date"
          rules={[{ required: true, message: "" }]}
        >
          <DatePicker
            placeholder="Choose Incentive End Date"
            style={{ width: 350 }}
          />
        </Form.Item>
        <Form.Item
          label="Modality"
          name="modality"
          rules={[{ required: true, message: "" }]}
        >
          <Checkbox.Group style={{ width: "100%" }}>
            <Flex
              align="center"
              justify="space-around"
              style={{ width: "100%" }}
            >
              <Checkbox value="XRAY">X - Ray</Checkbox>
              <Checkbox value="CT">CT</Checkbox>
              <Checkbox value="MRI">MRI</Checkbox>
              <Checkbox value="NM">NM</Checkbox>
            </Flex>
          </Checkbox.Group>
        </Form.Item>
        <Form.Item
          name="incentiveType"
          label="Incentive Type"
          rules={[{ required: true, message: "" }]}
        >
          <Select>
            <Select.Option value={IncentiveType.PER_CASE}>
              Per Case Type
            </Select.Option>
            <Select.Option value={IncentiveType.COMMISSION}>
              Commission
            </Select.Option>
          </Select>
        </Form.Item>

        {incentiveType && incentiveType === IncentiveType.COMMISSION && (
          <Form.Item
            name="commission"
            rules={[{ required: true, message: "" }]}
          >
            <Input placeholder="Commission %" />
          </Form.Item>
        )}
        {incentiveType && incentiveType === IncentiveType.PER_CASE && (
          <>
            <Flex justify="space-between" style={{ margin: "0.5rem 0" }}>
              <Row style={{ width: "50%" }}>
                <Typography.Text>Cases</Typography.Text>
              </Row>
              <Row style={{ width: "50%" }}>
                <Typography.Text>Amount</Typography.Text>
              </Row>
            </Flex>

            <Form.List
              name="incentiveTypePerCase"
              rules={[
                {
                  validator: async (_, incentiveTypePerCase) => {
                    if (
                      !incentiveTypePerCase ||
                      incentiveTypePerCase.length < 1
                    ) {
                      return Promise.reject(
                        new Error("At least 1 row required")
                      );
                    }
                  },
                },
              ]}
            >
              {(fields, { add, remove }, { errors }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space
                      key={key}
                      style={{ display: "flex", marginBottom: 8 }}
                      align="baseline"
                    >
                      <Form.Item
                        {...restField}
                        name={[name, "cases"]}
                        rules={[
                          { required: true, message: "", type: "number" },
                        ]}
                        style={{ width: 160 }}
                      >
                        <InputNumber
                          placeholder="Cases"
                          style={{ width: 160 }}
                        />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, "amount"]}
                        rules={[
                          { required: true, message: "", type: "number" },
                        ]}
                        style={{ width: 160 }}
                      >
                        <InputNumber
                          placeholder="Amount"
                          style={{ width: 160 }}
                        />
                      </Form.Item>
                      <MinusCircleOutlined onClick={() => remove(name)} />
                    </Space>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                    >
                      Add field
                    </Button>
                    <Form.ErrorList errors={errors} />
                  </Form.Item>
                </>
              )}
            </Form.List>
          </>
        )}
      </Form>
    </StyledModal>
  );
};

export default AcceptReferralModal;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    padding-top: 0.2rem;
  }
`;
